"use client";

import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Filters } from "@/components/ui/filters";
import { Icon, type IconName } from "@/components/ui/icon";
import { SocialNetworkIcon } from "@/components/ui/social-network-icon";
import useIsMobile from "@/hooks/use-is-mobile";
import { usePruneMissingFilterValues } from "@/hooks/use-prune-missing-filter-values";
import { getPipelineFilterOptions } from "@/lib/api/generated";
import type { PipelineFilterFacet } from "@/lib/api/model/pipelineFilterFacet";
import type { PresetItemResponse } from "@/lib/api/model/presetItemResponse";
import { cn } from "@/lib/utils";

import { toSocialNetwork } from "@/features/candidate/lib/socials";
import { ENROLLMENT_STATUS_META } from "@/features/enrollment/lib/status-meta";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import {
  addEntity,
  addTerm,
  ATS_SYNC_STATE_VALUES,
  EMPTY_TERM_SET,
  ENROLLMENT_STATUS_VALUES,
  lowerBoundOf,
  MAX_EXPERIENCE_YEARS,
  MIN_DEGREE_VALUES,
  ORIGIN_VALUES,
  SOCIAL_NETWORK_VALUES,
  termCount,
  termPatch,
  termSet,
  upperBoundOf,
  usePipelineFilterPanel,
  type PipelineAtsSyncState,
  type PipelineEnrollmentStatus,
  type PipelineFilterPanelApi,
  type PipelineMinDegree,
  type PipelineOrigin,
  type PipelineSocialNetwork,
} from "@/features/jobs/hooks/use-pipeline-filters";
import { ApplyPreset, type PresetTypeSlug } from "@/features/presets";
import { sequenceColor } from "@/features/sequences/lib/colors";

import {
  AutocompleteTermsField,
  CheckboxOptionList,
  EntityCardsField,
  ExperienceRangeField,
  GradYearRangeField,
  PipelineFilterSection,
  SearchSelectField,
  SingleSelectField,
  TermChipsField,
  TimeRangeField,
  TristateField,
  type CheckboxOption,
  type PickerOption,
} from "./shared";

const SOCIAL_NETWORK_LABELS: Record<PipelineSocialNetwork, string> = {
  linkedin: "Social",
  github: "GitHub",
  twitter: "X / Twitter",
  facebook: "Facebook",
};

const MIN_DEGREE_LABELS: Record<PipelineMinDegree, string> = {
  associates: "Associates",
  bachelors: "Bachelors",
  masters: "Masters",
  phd: "PhD",
};

const MIN_DEGREE_OPTIONS = MIN_DEGREE_VALUES.map((value) => ({
  value,
  label: MIN_DEGREE_LABELS[value],
}));

/**
 * Contact presence is read off the contacts this org holds — revealed, imported,
 * or ATS-synced — not off a provider's claim about what exists. Said plainly,
 * because "Has Email: No" invites reading as "unreachable".
 */
const CONTACT_TOOLTIP = (
  <>
    Counts contacts you already hold, from a reveal or an import. An unrevealed
    profile reads as <b>No</b> even when a reveal would find one.
  </>
);

type OriginGlyph = { name: IconName; className: string };

const SEARCH_GLYPH: OriginGlyph = {
  name: "IconSearchIntelligence",
  className: "text-brand",
};
const IMPORT_GLYPH: OriginGlyph = {
  name: "IconArrowOutOfBox",
  className: "text-[#7c3aed] dark:text-[#a78bfa]",
};

/**
 * The origin marks the pipeline table and the candidate sheet already use, so a
 * filter row reads as the badge it will match. The `upload_*` values only reach
 * here from a link saved before they were grouped under `profile_import`; they
 * share its glyph because they always shared its badge.
 */
const ORIGIN_ICONS: Record<PipelineOrigin, OriginGlyph> = {
  search: SEARCH_GLYPH,
  sourced: SEARCH_GLYPH,
  ats: {
    name: "IconPuzzle",
    className: "text-emerald-600 dark:text-emerald-400",
  },
  profile_import: IMPORT_GLYPH,
  upload_pdf: IMPORT_GLYPH,
  upload_docx: IMPORT_GLYPH,
  upload_rtf: IMPORT_GLYPH,
  upload_txt: IMPORT_GLYPH,
  upload_image: IMPORT_GLYPH,
  upload_csv: IMPORT_GLYPH,
};

/**
 * Labels for a value the backend didn't report a facet for — it has no rows on
 * this job, so no count and no label came back with it. Same words the facets
 * carry, so a zero-count row reads like the rest of the list.
 */
const ORIGIN_LABELS: Record<PipelineOrigin, string> = {
  sourced: "Unspecified",
  search: "Talent Search",
  ats: "ATS Import",
  profile_import: "Profile Import",
  upload_pdf: "Profile Import",
  upload_docx: "Profile Import",
  upload_rtf: "Profile Import",
  upload_txt: "Profile Import",
  upload_image: "Profile Import",
  upload_csv: "Profile Import",
};

const ATS_SYNC_STATE_LABELS: Record<PipelineAtsSyncState, string> = {
  not_synced: "Not Synced",
  queued: "Queued",
  synced: "Synced",
  error: "Error",
  ineligible: "Ineligible",
};

/**
 * Orders facet-derived options by the fixed vocabulary (so the list doesn't
 * reshuffle as counts change) and drops values the backend didn't report — a
 * value with no rows on this job would only ever return an empty table.
 *
 * A value that is already `selected` is kept regardless. These are fixed slugs
 * rather than ids, so one with no rows is a filter that still means something
 * and can't be pruned away; hiding its row would leave the table narrowed with
 * no checkbox to explain it.
 */
function facetOptions<T extends string>(
  order: readonly T[],
  facets: PipelineFilterFacet[] | undefined,
  selected: readonly T[],
  labels: Record<T, string>,
): CheckboxOption<T>[] {
  const byValue = new Map((facets ?? []).map((facet) => [facet.value, facet]));
  const picked = new Set<T>(selected);
  return order
    .filter((value) => byValue.has(value) || picked.has(value))
    .map((value) => {
      const facet = byValue.get(value);
      return {
        value,
        label: facet?.label ?? labels[value],
        count: facet?.count ?? 0,
      };
    });
}

/**
 * Drop stage and sequence filters whose entity is gone — a stage deleted since
 * the link was made, a sequence removed from the job.
 *
 * Sits beside the sidebar rather than inside its content, which isn't mounted
 * while the mobile drawer is closed. A stale filter narrows the table either
 * way, so it has to be dropped whether or not the panel is on screen.
 */
function usePruneMissingPipelineValues(panel: PipelineFilterPanelApi): void {
  const { job, stages } = usePipelineJob();
  const { state, set } = panel;

  // Nothing to check means nothing to fetch: the panel's own copy of this query
  // shares the key, so opening it doesn't ask twice.
  const hasSequenceFilters =
    state.sequences.length > 0 || state.excludeSequences.length > 0;
  const optionsQuery = useQuery({
    queryKey: ["pipeline", job.id, "filter-options"],
    queryFn: () => getPipelineFilterOptions(job.id),
    staleTime: 60_000,
    enabled: hasSequenceFilters,
  });

  // Stages arrive with the pipeline response, so an empty list is one that
  // hasn't loaded — a job always keeps its stages.
  const stageIds = useMemo(
    () => (stages.length > 0 ? stages.map((stage) => stage.id) : null),
    [stages],
  );
  // Every sequence of the job, archived included, so absence here means the
  // sequence is gone rather than merely not worth offering.
  // Nullish rather than falsy: a job with no sequences reports an empty list,
  // and any sequence filter on it really is stale — but a response that omits
  // the field says nothing, and must not read as "none exist".
  const sequenceIds = useMemo(
    () =>
      optionsQuery.data?.sequences?.map((sequence) => sequence.sequence_id) ??
      null,
    [optionsQuery.data?.sequences],
  );

  usePruneMissingFilterValues(
    { stages: state.stages, excludeStages: state.excludeStages },
    stageIds,
    set,
  );
  usePruneMissingFilterValues(
    { sequences: state.sequences, excludeSequences: state.excludeSequences },
    sequenceIds,
    set,
  );
}

function PipelineFilterContent({ panel }: { panel: PipelineFilterPanelApi }) {
  const { job, stages } = usePipelineJob();
  const { state, set, clearAll, hasAnyApplied, stageFiltersApply } = panel;
  const [presetSheet, setPresetSheet] = useState<PresetTypeSlug | null>(null);

  // Origins and sync states come from the job's own rows, so the panel never
  // offers a value that matches nothing; sequences come from the job itself, so
  // one nobody is enrolled in yet is still selectable. Cached for a minute: the
  // facets shift only when candidates are added, synced, or enrolled.
  const optionsQuery = useQuery({
    queryKey: ["pipeline", job.id, "filter-options"],
    queryFn: () => getPipelineFilterOptions(job.id),
    staleTime: 60_000,
  });
  const options = optionsQuery.data;

  const stageOptions = useMemo<PickerOption[]>(
    () =>
      stages.map((stage) => ({
        value: stage.id,
        label: stage.name,
        count: stage.candidate_count,
        color: stage.color,
      })),
    [stages],
  );

  // Sequences carry no stored colour, so the swatch is derived from the id the
  // same way the table's sequence pills are — one sequence, one colour, panel
  // and table alike.
  //
  // An archived sequence takes no new enrollments, so it is not offered as a
  // fresh pick — but one already filtered on stays listed, which is what gives
  // its chip a name instead of a bare id.
  const sequenceOptions = useMemo<PickerOption[]>(() => {
    const picked = new Set([...state.sequences, ...state.excludeSequences]);
    return (options?.sequences ?? [])
      .filter(
        (sequence) => !sequence.archived || picked.has(sequence.sequence_id),
      )
      .map((sequence) => ({
        value: sequence.sequence_id,
        label: sequence.name,
        count: sequence.count,
        color: sequenceColor(sequence.sequence_id),
      }));
  }, [options?.sequences, state.sequences, state.excludeSequences]);

  const originOptions = useMemo(
    () =>
      facetOptions<PipelineOrigin>(
        ORIGIN_VALUES,
        options?.origins,
        state.origins,
        ORIGIN_LABELS,
      ).map((option) => {
        const glyph = ORIGIN_ICONS[option.value];
        return {
          ...option,
          icon: <Icon name={glyph.name} size={14} className={glyph.className} />,
        };
      }),
    [options?.origins, state.origins],
  );

  const syncStateOptions = useMemo(
    () =>
      facetOptions<PipelineAtsSyncState>(
        ATS_SYNC_STATE_VALUES,
        options?.ats_sync_states,
        state.atsSyncStates,
        ATS_SYNC_STATE_LABELS,
      ),
    [options?.ats_sync_states, state.atsSyncStates],
  );

  // Status labels, icons and colours come from the enrollment feature's own
  // map, so a filter row reads exactly like the status pill it will match.
  const enrollmentStatusOptions = useMemo<
    CheckboxOption<PipelineEnrollmentStatus>[]
  >(
    () =>
      ENROLLMENT_STATUS_VALUES.map((value) => {
        const meta = ENROLLMENT_STATUS_META[value];
        return {
          value,
          label: meta.label,
          icon: <Icon name={meta.icon} size={14} className={meta.color} />,
        };
      }),
    [],
  );

  const socialNetworkOptions = useMemo<PickerOption[]>(
    () =>
      SOCIAL_NETWORK_VALUES.map((value) => {
        const network = toSocialNetwork(value);
        return {
          value,
          label: SOCIAL_NETWORK_LABELS[value],
          icon: network ? (
            <SocialNetworkIcon network={network} className="size-3.5" />
          ) : undefined,
        };
      }),
    [],
  );

  // A link saved when the relative window was its own control still carries it;
  // these are the same readings the compiler uses, so the range shown is the
  // range applied.
  const addedFrom = lowerBoundOf(state.addedFrom, state.addedWithin);
  const addedTo = upperBoundOf(state.addedTo);
  const inStageFrom = lowerBoundOf(state.inStageFrom, state.inStageWithin);
  const inStageTo = upperBoundOf(state.inStageTo);

  const sequenceCount =
    state.sequences.length +
    state.excludeSequences.length +
    state.enrollmentStatuses.length +
    (state.enrolled !== null ? 1 : 0);
  const contactCount = [state.revealed, state.hasEmail, state.hasPhone].filter(
    (v) => v !== null,
  ).length;
  const educationCount =
    state.universities.length +
    termCount(state, "majors", "universityLocations") +
    (state.minDegree ? 1 : 0) +
    (state.gradYearStart !== null || state.gradYearEnd !== null ? 1 : 0);

  /**
   * Presets carry names, not cards, so applying one goes through the same add
   * path as typing: capped, de-duped, and defaulted to an optional chip.
   */
  const applyPreset = useCallback(
    (type: PresetTypeSlug, items: PresetItemResponse[]) => {
      if (type === "companies" || type === "universities") {
        const field = type === "companies" ? "companies" : "universities";
        let next = state[field];
        for (const item of items) {
          next = addEntity(next, { text: item.name, domain: item.domain });
        }
        set({ [field]: next });
        return;
      }
      if (type === "skills" || type === "locations") {
        const field = type === "skills" ? "skills" : "locations";
        let next = termSet(state, field);
        for (const item of items) {
          // Skills are stored lowercase to match the autocomplete index;
          // locations keep the casing they were saved with.
          next = addTerm(next, type === "skills" ? item.name.toLowerCase() : item.name);
        }
        set(termPatch(field, next));
      }
    },
    [set, state],
  );

  return (
    <>
      {/* h-13 (52px) so the header sits flush with the toolbar's search row,
          which the rail now starts alongside. */}
      <Filters.Header className="h-13" />

      <Filters.Body>
        <PipelineFilterSection
          icon="IconPeopleCircle"
          label="Candidate"
          defaultOpen
          activeCount={termCount(state, "names", "emails")}
          onClear={() =>
            set({ ...termPatch("names", EMPTY_TERM_SET), ...termPatch("emails", EMPTY_TERM_SET) })
          }
        >
          <TermChipsField
            label="Name"
            placeholder="e.g. Ada Lovelace"
            value={termSet(state, "names")}
            onChange={(next) => set(termPatch("names", next))}
          />
          <TermChipsField
            label="Email"
            placeholder="e.g. ada@acme.com, or acme.com"
            inputType="email"
            value={termSet(state, "emails")}
            onChange={(next) => set(termPatch("emails", next))}
          />
        </PipelineFilterSection>

        {/* Only on "All": with a stage selected in the bar above, the request is
            already scoped to it and a second stage filter could only contradict
            that. */}
        {stageFiltersApply && (
          <PipelineFilterSection
            icon="IconStage"
            label="Stage"
            activeCount={state.stages.length + state.excludeStages.length}
            onClear={() => set({ stages: [], excludeStages: [] })}
          >
            <SearchSelectField
              label="Stage"
              placeholder="Select Stage"
              options={stageOptions}
              selected={state.stages}
              onChange={(stages) => set({ stages })}
              excluded={state.excludeStages}
              onExcludedChange={(excludeStages) => set({ excludeStages })}
              emptyMessage="This job has no stages yet."
              noMatchMessage="No stages match"
              showSwatch
            />
          </PipelineFilterSection>
        )}

        <PipelineFilterSection
          icon="IconPaperPlane"
          label="Outreach Sequences"
          activeCount={sequenceCount}
          onClear={() =>
            set({
              sequences: [],
              excludeSequences: [],
              enrollmentStatuses: [],
              enrolled: null,
            })
          }
        >
          <TristateField
            label="In a Live Sequence"
            value={state.enrolled}
            onChange={(enrolled) => set({ enrolled })}
            tooltip="Outreach that hasn't finished: scheduled, active, or paused. Not
              only actively sending — for one exact state, use Enrollment Status."
          />
          <SearchSelectField
            label="Sequence"
            placeholder="Select Sequence"
            options={sequenceOptions}
            selected={state.sequences}
            onChange={(sequences) => set({ sequences })}
            excluded={state.excludeSequences}
            onExcludedChange={(excludeSequences) => set({ excludeSequences })}
            emptyMessage="This job has no sequences yet."
            noMatchMessage="No sequences match"
            showSwatch
          />
          <Filters.FieldContainer>
            <Filters.FieldLabel>Enrollment Status</Filters.FieldLabel>
            <Filters.FieldContent>
              <CheckboxOptionList
                options={enrollmentStatusOptions}
                selected={state.enrollmentStatuses}
                onChange={(enrollmentStatuses) => set({ enrollmentStatuses })}
              />
            </Filters.FieldContent>
          </Filters.FieldContainer>
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconLineChart3"
          label="Experience"
          activeCount={
            state.minExperienceYears !== null ||
            state.maxExperienceYears !== null
              ? 1
              : 0
          }
          onClear={() =>
            set({ minExperienceYears: null, maxExperienceYears: null })
          }
        >
          <ExperienceRangeField
            min={state.minExperienceYears}
            max={state.maxExperienceYears}
            capYears={MAX_EXPERIENCE_YEARS}
            buckets={options?.experience_years ?? []}
            onChange={({ min, max }) =>
              set({ minExperienceYears: min, maxExperienceYears: max })
            }
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconBag"
          label="Job Title"
          activeCount={termCount(state, "jobTitles")}
          onClear={() => set(termPatch("jobTitles", EMPTY_TERM_SET))}
        >
          <TermChipsField
            label="Current Title"
            placeholder="Add a title..."
            value={termSet(state, "jobTitles")}
            onChange={(next) => set(termPatch("jobTitles", next))}
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconBuildings"
          label="Company"
          activeCount={state.companies.length}
          onClear={() => set({ companies: [] })}
        >
          <EntityCardsField
            label="Companies"
            field="companies"
            entities={state.companies}
            onChange={(companies) => set({ companies })}
            presetLabel="Company Presets"
            onOpenPreset={() => setPresetSheet("companies")}
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconMapPin"
          label="Location"
          activeCount={termCount(state, "locations")}
          onClear={() => set(termPatch("locations", EMPTY_TERM_SET))}
        >
          {/* The same suggestions Talent Search offers, and the full
              "City, Region, Country" value they read as: a candidate's stored
              location is that same string, so the specific suggestion matches
              exactly while a country on its own still matches everyone in it. */}
          <AutocompleteTermsField
            label="Location"
            field="locations"
            value={termSet(state, "locations")}
            onChange={(next) => set(termPatch("locations", next))}
            presetLabel="Location Presets"
            onOpenPreset={() => setPresetSheet("locations")}
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconGraduateCap"
          label="Education"
          activeCount={educationCount}
          onClear={() =>
            set({
              universities: [],
              minDegree: null,
              gradYearStart: null,
              gradYearEnd: null,
              ...termPatch("majors", EMPTY_TERM_SET),
              ...termPatch("universityLocations", EMPTY_TERM_SET),
            })
          }
        >
          <EntityCardsField
            label="Universities"
            field="universities"
            entities={state.universities}
            onChange={(universities) => set({ universities })}
            presetLabel="University Presets"
            onOpenPreset={() => setPresetSheet("universities")}
          />
          <AutocompleteTermsField
            label="University Locations"
            field="locations"
            value={termSet(state, "universityLocations")}
            onChange={(next) => set(termPatch("universityLocations", next))}
            // The backend matches each term as a substring of the school's
            // city / region / country, so a suggestion contributes its most
            // specific part — the full "City, Region, Country" string it reads
            // as would match none of those columns on its own.
            selectValue={(result) => result.value.split(", ")[0] ?? result.value}
          />
          <GradYearRangeField
            start={state.gradYearStart}
            end={state.gradYearEnd}
            onChange={({ start, end }) =>
              set({ gradYearStart: start, gradYearEnd: end })
            }
          />
          <AutocompleteTermsField
            label="Majors"
            field="majors"
            value={termSet(state, "majors")}
            onChange={(next) => set(termPatch("majors", next))}
            lowercase
          />
          <SingleSelectField
            label="Minimum Degree"
            placeholder="No Minimum"
            options={MIN_DEGREE_OPTIONS}
            value={state.minDegree}
            onChange={(minDegree) => set({ minDegree })}
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconWhiteboard"
          label="Skills"
          activeCount={termCount(state, "skills")}
          onClear={() => set(termPatch("skills", EMPTY_TERM_SET))}
        >
          <AutocompleteTermsField
            label="Skills"
            field="skills"
            value={termSet(state, "skills")}
            onChange={(next) => set(termPatch("skills", next))}
            presetLabel="Skill Presets"
            onOpenPreset={() => setPresetSheet("skills")}
            lowercase
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconChainLink1"
          label="Socials"
          activeCount={
            termCount(state, "socialUrls") +
            state.socialNetworks.length +
            state.excludeSocialNetworks.length
          }
          onClear={() =>
            set({
              ...termPatch("socialUrls", EMPTY_TERM_SET),
              socialNetworks: [],
              excludeSocialNetworks: [],
            })
          }
        >
          <TermChipsField
            label="Profile URL or Handle"
            placeholder="e.g. linkedin.com/in/adalovelace"
            value={termSet(state, "socialUrls")}
            onChange={(next) => set(termPatch("socialUrls", next))}
            tooltip={
              <>
                Matched against the stored link or handle, so any part of one
                works: <b>linkedin.com/in/adalovelace</b>, <b>adalovelace</b>, or
                a full <b>https://</b> link. Not a name or an email — those have
                their own fields above.
              </>
            }
          />
          <SearchSelectField
            label="Has a Profile On"
            placeholder="Select Network"
            options={socialNetworkOptions}
            selected={state.socialNetworks}
            onChange={(socialNetworks) =>
              set({ socialNetworks: socialNetworks as PipelineSocialNetwork[] })
            }
            excluded={state.excludeSocialNetworks}
            onExcludedChange={(excludeSocialNetworks) =>
              set({
                excludeSocialNetworks:
                  excludeSocialNetworks as PipelineSocialNetwork[],
              })
            }
            noMatchMessage="No networks match"
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconContacts"
          label="Contact Information"
          activeCount={contactCount}
          onClear={() =>
            set({ revealed: null, hasEmail: null, hasPhone: null })
          }
        >
          <TristateField
            label="Contact Revealed"
            value={state.revealed}
            onChange={(revealed) => set({ revealed })}
          />
          <TristateField
            label="Has Email"
            value={state.hasEmail}
            onChange={(hasEmail) => set({ hasEmail })}
            tooltip={CONTACT_TOOLTIP}
          />
          <TristateField
            label="Has Phone"
            value={state.hasPhone}
            onChange={(hasPhone) => set({ hasPhone })}
            tooltip={CONTACT_TOOLTIP}
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconArrowInbox"
          label="Origin"
          activeCount={state.origins.length}
          onClear={() => set({ origins: [] })}
        >
          <CheckboxOptionList
            options={originOptions}
            selected={state.origins}
            onChange={(origins) => set({ origins })}
            emptyMessage="No candidates on this job yet."
          />
        </PipelineFilterSection>

        {syncStateOptions.length > 0 && (
          <PipelineFilterSection
            icon="IconCloudSync"
            label="ATS Sync"
            activeCount={state.atsSyncStates.length}
            onClear={() => set({ atsSyncStates: [] })}
          >
            <CheckboxOptionList
              options={syncStateOptions}
              selected={state.atsSyncStates}
              onChange={(atsSyncStates) => set({ atsSyncStates })}
            />
          </PipelineFilterSection>
        )}

        <PipelineFilterSection
          icon="IconCalendar1"
          label="Added to Pipeline"
          activeCount={addedFrom !== null || addedTo !== null ? 1 : 0}
          onClear={() =>
            set({ addedWithin: null, addedFrom: null, addedTo: null })
          }
        >
          <TimeRangeField
            label="Added"
            from={addedFrom}
            to={addedTo}
            onChange={({ from, to }) =>
              // Any write lands on the bounds, retiring the standalone window a
              // link may still carry.
              set({ addedFrom: from, addedTo: to, addedWithin: null })
            }
          />
        </PipelineFilterSection>

        <PipelineFilterSection
          icon="IconStopwatch"
          label="Time in Stage"
          activeCount={inStageFrom !== null || inStageTo !== null ? 1 : 0}
          onClear={() =>
            set({ inStageWithin: null, inStageFrom: null, inStageTo: null })
          }
        >
          <TimeRangeField
            label="Entered stage"
            from={inStageFrom}
            to={inStageTo}
            onChange={({ from, to }) =>
              set({ inStageFrom: from, inStageTo: to, inStageWithin: null })
            }
          />
        </PipelineFilterSection>

      </Filters.Body>

      <Filters.Footer>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={clearAll}
          disabled={!hasAnyApplied}
          className="relative h-7 min-h-7 w-full border border-contrast/30 bg-transparent shadow-none hover:border-contrast/50 hover:bg-transparent active:bg-transparent disabled:opacity-40"
        >
          <span className="text-sm font-medium leading-5 text-contrast/60">
            Clear all filters
          </span>
        </Button>
      </Filters.Footer>

      {/* The Talent Search preset sheet, applied through a callback so it writes
          to these filters instead of that panel's own state. */}
      {presetSheet && (
        <ApplyPreset
          open
          presetType={presetSheet}
          onClose={() => setPresetSheet(null)}
          onApplyItems={(items) => applyPreset(presetSheet, items)}
        />
      )}
    </>
  );
}

export type PipelineFilterSidebarProps = {
  className?: string;
  /** Controlled open state for the mobile drawer; ignored on desktop. */
  mobileOpen?: boolean;
  onMobileOpenChange?: (next: boolean) => void;
};

/**
 * Filter rail to the left of the pipeline, collapsed by default so the table
 * keeps its full width until someone reaches for filters.
 *
 * Below `lg` the rail is replaced by a drawer opened from
 * <PipelineFilterButton>, matching the tasks sidebar.
 */
export function PipelineFilterSidebar({
  className,
  mobileOpen = false,
  onMobileOpenChange,
}: PipelineFilterSidebarProps) {
  const panel = usePipelineFilterPanel();
  const [collapsed, setCollapsed] = useState(true);
  const isMobile = useIsMobile();

  usePruneMissingPipelineValues(panel);

  // In the drawer the <Filters.Header> chevron should close the drawer rather
  // than collapse to an icon rail — there is no rail on mobile.
  const handleMobileCollapsedChange = useCallback(
    (next: boolean) => {
      if (next) onMobileOpenChange?.(false);
    },
    [onMobileOpenChange],
  );

  return (
    <>
      {!isMobile && (
        <Filters.Container
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          collapsedCount={panel.appliedCount}
          dataSlot="pipeline-filter-sidebar"
          // `useIsMobile` is not eager, so the first client paint renders this
          // desktop branch even on a phone; the CSS gate keeps it off screen
          // until the effect corrects. Inert at `lg+`.
          className={cn("max-lg:hidden", className)}
        >
          <PipelineFilterContent panel={panel} />
        </Filters.Container>
      )}

      {isMobile && (
        <Drawer
          direction="left"
          open={mobileOpen}
          onOpenChange={(next) => onMobileOpenChange?.(next)}
        >
          <DrawerContent className="w-[85vw] max-w-[371px] border-r border-border bg-surface-primary data-[vaul-drawer-direction=left]:w-[85vw] data-[vaul-drawer-direction=left]:sm:max-w-[371px]">
            <DrawerTitle className="sr-only">Pipeline filters</DrawerTitle>
            <Filters.Container
              collapsed={false}
              onCollapsedChange={handleMobileCollapsedChange}
              dataSlot="pipeline-filter-sidebar"
              renderChildrenOnly
            >
              <PipelineFilterContent panel={panel} />
            </Filters.Container>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}

/**
 * Mobile-only trigger for the filter drawer, with the applied-filter count.
 * Renders nothing on desktop, where the rail is always present.
 */
export function PipelineFilterButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  const isMobile = useIsMobile();
  const { appliedCount } = usePipelineFilterPanel();

  if (!isMobile) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open filters"
      className={cn(
        "flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-contrast/5 bg-contrast/3 px-2 text-sm font-medium text-heading transition-colors hover:bg-contrast/6",
        className,
      )}
    >
      <Icon name="IconFilter2" size={16} className="text-heading opacity-60" />
      {appliedCount > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded border-[0.714px] border-[#ffaf53]/25 bg-[#ffaf53]/15 px-1 text-[11px] font-medium leading-[14.286px] tabular-nums text-[#ffaf53]">
          {appliedCount}
        </span>
      )}
    </button>
  );
}
